'use strict';

const radicalShoppingCart = require('../../modules/helpers/radicalShoppingCart').getInstance();
const tracking = require('../../modules/utils/tracking.utils');

const VALIDATIONS = {
    MANDATORY_SELECTION: 'MANDATORY_SELECTION',
    NAG: 'NAG',
    NONE: 'NONE',
    PENDING_DECISION: 'PENDING_DECISION',
};

const Nag = require('../../modules/ui/nag/nag');

const isMandatorySelectionEnabled = (collection) => collection.some((model) => model.isMandatorySelectionFlagEnabled());

const isContinuanceNagEnabled = (collection) => collection.some((model) => model.isContinuanceNagFlagEnabled());

const isPendingDecisionEnabled = (collection) => collection.some((model) => model.isPendingDecisionEnabled());

const isEmptyValidation = () => true;

const validationRules = [
    {
        should: isPendingDecisionEnabled,
        shoot: VALIDATIONS.PENDING_DECISION,
    },
    {
        should: isContinuanceNagEnabled,
        shoot: VALIDATIONS.NAG,
    },
    {
        should: isMandatorySelectionEnabled,
        shoot: VALIDATIONS.MANDATORY_SELECTION,
    },
    {
        should: isEmptyValidation,
        shoot: VALIDATIONS.NONE,
    },
];

const findValidationRule = (collection) => validationRules.find((rule) => rule.should(collection)) || {};

const getContinueValidationType = (collection) => {
    return findValidationRule(collection).shoot || VALIDATIONS.NONE;
};

module.exports = Odigeo.Collection.extend({
    isLoadedPromise: null,
    selectionCache: {},
    connect: true,

    init() {
        this.isLoadedPromise = $.Deferred();
        this.setUrl();
    },

    getPage(page) {
        return page || this.widget.options.page;
    },

    setUrl(page) {
        this.url = Odigeo.Settings.getProperty('ancillariesUrlFormatter')('availables/' + this.getPage(page));
    },

    onError(data, xhr) {
        this.resolvePromise(false);
        this.widget.tracking.trackOfferedProducts(null);
        tracking.counterMetric(this.widget.getErrorMetricKey());
    },

    getOfferPolicies(model) {
        return model.getOffersList().reduce((policies, offer) => {
            return [...policies, Odigeo._.get(offer, 'policy')];
        }, []);
    },

    getPolicies() {
        return this.reduce((policies, model) => {
            model.afterSync();
            return [...policies, ...this.getOfferPolicies(model)];
        }, []);
    },

    onSuccess() {
        this.resolvePromise(true);
        this.widget.tracking.trackOfferedProducts(this.getPolicies(), false, Odigeo.AB.getDelayCompensation > 1 ? ['DELAY'] : []);
    },

    resolvePromise(isSuccessful) {
        this.isLoadedPromise.resolve(isSuccessful);
    },

    fetchData() {
        this.isLoadedPromise = $.Deferred();
        this.widget.tracking.willTrackOnLoadOfSomeProducts();

        this.fetch({
            error: Odigeo._.bind(this.onError, this),
            success: Odigeo._.bind(this.onSuccess, this),
        });
        return this.isLoadedPromise;
    },

    isSelectionValid(skipNagWhenAnyProductIsSelected) {
        switch (getContinueValidationType(this)) {
            case VALIDATIONS.MANDATORY_SELECTION:
                return this.validateContinueMandatorySelectionDisplay();
            case VALIDATIONS.NAG:
                return this.validateContinueNagDisplay(skipNagWhenAnyProductIsSelected);
            case VALIDATIONS.PENDING_DECISION:
                return true;
        }

        return false;
    },

    validateSelection(skipNagWhenAnyProductIsSelected) {
        return {
            'validationType': getContinueValidationType(this),
            'invalid': this.isSelectionValid(skipNagWhenAnyProductIsSelected),
        };
    },

    validateContinueMandatorySelectionDisplay() {
        return !this.isAnyProductSelected() && this.isMandatorySelectionValidationErrorDisplayFailed();
    },

    isAnyProductSelectedCondition(skipNagWhenAnyProductIsSelected) {
        return skipNagWhenAnyProductIsSelected ? !this.isAnyProductSelected() : true;
    },

    validateContinueNagDisplay(skipNagWhenAnyProductIsSelected) {
        return this.isAnyProductSelectedCondition(skipNagWhenAnyProductIsSelected)
            && !Odigeo._.isUndefined(this.getFirstModelToDisplayContinuanceNag());
    },

    getFirstModelToDisplayContinuanceNag() {
        const continuanceNagGroupsExcluded = this.getContinuanceNagGroupsExcluded();

        return Odigeo._.first(this.getModelsToDisplayContinuanceNag().filter((modelToDisplayContinuanceNag) => {
            return !Odigeo._.includes(continuanceNagGroupsExcluded, modelToDisplayContinuanceNag.getNagContinueGroup());
        }));
    },

    getModelsToDisplayContinuanceNag() {
        return this.filter((modelToCheck) => modelToCheck.hasToDisplayContinuanceNag());
    },

    getContinuanceNagGroupsExcluded() {
        return Odigeo._.compact(this.map((ancillaryModel) => {
            return ancillaryModel.getAddedPolicy() ? ancillaryModel.getNagContinueGroup() : null;
        }));
    },

    getAddedPolicies() {
        return Odigeo._.compact(this.map((ancillaryModel) => ancillaryModel.getAddedPolicy()));
    },

    configureShoppingCart(nextPage) {
        radicalShoppingCart.configureShoppingCart('ancillary', this.widget.id, this.get('bookingId'), nextPage);
    },

    updateShoppingCart() {
        this.each((model) => model.updateProductStateOverRadicalShoppingCart());
    },

    submitShoppingCart() {
        radicalShoppingCart.submitProductPerPage();
    },

    updateProductsStateOverRadicalShoppingCartAndContinue(nextPage) {
        this.configureShoppingCart(nextPage);
        this.updateShoppingCart();
        this.submitShoppingCart();
    },

    getModelByPolicy(policy) {
        return this.find((model) => !Odigeo._.isEmpty(model.getOfferByPolicy(policy)));
    },

    isPolicySelected(select, policy) {
        return !Odigeo._.isUndefined(select) ? select : !this.getModelByPolicy(policy).isSelected(policy);
    },

    toggleAncillaryOffer(policy, select) {
        const model = this.getModelByPolicy(policy);

        if (!Odigeo._.isUndefined(model)) {
            model.selectProduct({selected: this.isPolicySelected(select, policy), policy, silent: true});
        }
    },

    removeFlexInsuranceFromCache() {
        this.selectionCache['#FLEXRB156#'] = null;
    },

    isPolicyNotRejected(model) {
        const selectedPolicy = model.getSelectedPolicy();

        return !Odigeo._.isUndefined(selectedPolicy) && !model.isRejected(selectedPolicy);
    },

    isAnyProductSelected() {
        return this.reduce((isAnySelected, model) => isAnySelected || this.isPolicyNotRejected(model), false);
    },

    hasToShowValidationError() {
        return this.reduce((valid, model) => model.validateProductSelection() || valid, false);
    },

    isMandatorySelectionValidationErrorDisplayFailed() {
        const hasToShowValidationError = this.hasToShowValidationError();

        if (hasToShowValidationError) {
            this.each((model) => model.setMandatorySelectionErrorAsShown());
        }

        return hasToShowValidationError;
    },

    rejectSelectionForUnSelectedSingleProducts() {
        this.each((model) => model.rejectProductSelectionIfIsNotSelected());
    },

    showPendingDecisionError() {
        this.each((model) => model.triggerPendingDecisionError());
    },

    showContinueNag() {
        const modelToDisplayNag = this.getFirstModelToDisplayContinuanceNag();

        if (!Odigeo._.isUndefined(modelToDisplayNag)) {
            modelToDisplayNag.triggerAncillaryNag(this.getContinueNagType());
        }
    },

    getContinueNagType() {
        return Nag.NAG_TYPE.continuance;
    },

    isRefundableFareUpgradeSetInPax() {
        return Odigeo.Utils.parseJson(Odigeo.Session.get('isRefundableFareUpgradeSetInPax')) || false;
    },

    getLoadedPromise() {
        return this.isLoadedPromise.promise();
    },

    refresh() {
        return this.forEach((model) => model.afterSync());
    },

    loadBags(baggageData) {
        this.baggageData = baggageData;
    },
});
